import { useState } from "react";
import { useNavigate, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Logo } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Eye, EyeOff } from "lucide-react";

export default function Login() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showEmail, setShowEmail] = useState(true);

  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      const status = err.response?.status;
      if (!err.response) {
        setError("Unable to login. Please try again."); // network / server unreachable
      } else if (status === 401) {
        setError("Invalid username or password");
      } else if (status === 403) {
        setError("Account is inactive");
      } else {
        setError("Unable to login. Please try again."); // 5xx / unexpected
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:block relative">
        <img src="https://images.pexels.com/photos/13068377/pexels-photo-13068377.jpeg"
          alt="Salon" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0d1813] via-[#0d1813]/40 to-transparent" />
        <div className="absolute bottom-12 left-12 right-12">
          <Logo className="text-4xl" />
          <p className="font-display text-2xl mt-4 text-foreground/90">Redefining Beauty,<br />One Experience at a Time.</p>
        </div>
      </div>
      <div className="flex items-center justify-center p-6 bg-background">
        <Card className="w-full max-w-md p-8 bg-card border-border">
          <div className="lg:hidden mb-6"><Logo className="text-3xl" /></div>
          <h1 className="font-display text-3xl font-medium">Welcome back</h1>
          <p className="text-muted-foreground text-sm mt-1 mb-6">Sign in to <span className="text-primary font-medium" data-testid="login-app-name">Dynamic Billing</span> — the DEFINE SALONS management suite.</p>
          <form onSubmit={submit} className="space-y-4" autoComplete="off">
            <div>
              <Label htmlFor="email">Email</Label>
              <div className="relative mt-1.5">
                <Input id="email" name="ds-login-email" data-testid="login-email" type="email" value={email}
                  autoComplete="off" readOnly onFocus={(e) => e.target.removeAttribute("readonly")}
                  onChange={(e) => setEmail(e.target.value)}
                  style={showEmail ? undefined : { WebkitTextSecurity: "disc", textSecurity: "disc" }}
                  className="bg-secondary pr-10" required />
                <button type="button" data-testid="toggle-email-visibility"
                  onClick={() => setShowEmail((v) => !v)}
                  aria-label={showEmail ? "Hide email" : "Show email"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors">
                  {showEmail ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <div className="relative mt-1.5">
                <Input id="password" name="ds-login-password" data-testid="login-password" type={showPassword ? "text" : "password"} value={password}
                  autoComplete="new-password" readOnly onFocus={(e) => e.target.removeAttribute("readonly")}
                  onChange={(e) => setPassword(e.target.value)} className="bg-secondary pr-10" required />
                <button type="button" data-testid="toggle-password-visibility"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground transition-colors">
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>
            {error && <p className="text-destructive text-sm" data-testid="login-error">{error}</p>}
            <Button type="submit" data-testid="login-submit" disabled={loading}
              className="w-full rounded-sm bg-primary text-primary-foreground hover:bg-[#D4AF37] transition-transform duration-200 hover:-translate-y-0.5">
              {loading ? "Signing in…" : "Sign In"}
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
}
